import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** What the answer does, in a sentence the curator can weigh. A confirm that only asks "are you sure?" teaches the click. */
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  /** The confirm closes the dialog first, then runs, so a refusal's toast is not hidden behind it. */
  onConfirm: () => void;
  /** What the dialog asks for besides the sentence: a list, a choice. */
  children?: ReactNode;
  /** The confirm waits until the choice in `children` is made. */
  confirmDisabled?: boolean;
  /** Red for something that cannot be taken back (the default); `default` for a plain choice. */
  confirmVariant?: "default" | "destructive";
}

/**
 * The one confirmation the festival screen asks before something it cannot take back: rejecting a proposal, merging
 * two festivals. It says what will happen; the page's own toast says how it went.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  children,
  confirmDisabled = false,
  confirmVariant = "destructive",
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {children}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button
            variant={confirmVariant}
            disabled={confirmDisabled}
            onClick={() => {
              onOpenChange(false);
              onConfirm();
            }}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
